/**
 * Bet Slip Context
 * Global state for bet slip - persists across page navigation (EventDetail, Sports, Esports, etc.)
 * Bets stay in slip until user explicitly removes them.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { BetSlipItem } from '../types/sports';

interface BetSlipContextType {
  items: BetSlipItem[];
  setItems: React.Dispatch<React.SetStateAction<BetSlipItem[]>>;
  removeItem: (id: string) => void;
  clearAll: () => void;
}

const BetSlipContext = createContext<BetSlipContextType | undefined>(undefined);

export const BetSlipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<BetSlipItem[]>([]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
  }, []);

  const value: BetSlipContextType = {
    items,
    setItems,
    removeItem,
    clearAll,
  };

  return <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>;
};

export function useBetSlip(): BetSlipContextType {
  const context = useContext(BetSlipContext);
  if (context === undefined) {
    throw new Error('useBetSlip must be used within a BetSlipProvider');
  }
  return context;
}
